import { whenHover } from '@blocksuite/affine-components/hover';
import { RefNodeSlotsProvider } from '@blocksuite/affine-inline-reference';
import type { ReferenceInfo } from '@blocksuite/affine-model';
import { ImageProxyService } from '@blocksuite/affine-shared/adapters';
import {
  EditorSettingProvider,
  LinkPreviewServiceIdentifier,
  ParseDocUrlProvider,
  ToolbarRegistryIdentifier,
} from '@blocksuite/affine-shared/services';
import { affineTextStyles } from '@blocksuite/affine-shared/styles';
import type { AffineTextAttributes } from '@blocksuite/affine-shared/types';
import { normalizeUrl } from '@blocksuite/affine-shared/utils';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import type { BlockComponent, BlockStdScope } from '@blocksuite/std';
import { BLOCK_ID_ATTR, ShadowlessElement } from '@blocksuite/std';
import {
  INLINE_ROOT_ATTR,
  type InlineRootElement,
  ZERO_WIDTH_FOR_EMPTY_LINE,
} from '@blocksuite/std/inline';
import type { DeltaInsert } from '@blocksuite/store';
import { css, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { type StyleInfo, styleMap } from 'lit/directives/style-map.js';

export class AffineLink extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    affine-link a:hover [data-v-text='true'] {
      text-decoration: underline;
    }

    /* A decoration has no DOM content to select, edit, copy, or export. */
    affine-link a.affine-link-with-favicon::before {
      content: '';
      /* Inline padding keeps the icon attached to the first text fragment. */
      display: inline;
      padding-inline-start: 1em;
      margin-inline-end: 0.15em;
      background: var(--affine-link-favicon) left center / 1em 1em no-repeat;
      user-select: none;
      -webkit-user-select: none;
      pointer-events: none;
    }

    @media print {
      affine-link a.affine-link-with-favicon::before {
        content: none;
        display: none;
      }
    }
  `;

  @state()
  private accessor _faviconUrl: string | undefined;

  private _faviconImage: HTMLImageElement | null = null;
  private _requestedFaviconKey: string | null = null;
  private _faviconRequest = 0;

  private get _faviconRequestInfo() {
    const settings = this.std.getOptional(EditorSettingProvider)?.setting$
      .value;
    const enabled = settings?.showLinkFavicons ?? true;
    if (!enabled || !this.isLinkStart) return null;

    const link = this.link;
    if (!URL.canParse(link)) return null;
    const url = new URL(link);
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.username ||
      url.password ||
      url.origin === location.origin ||
      this.std.getOptional(ParseDocUrlProvider)?.parseDocUrl(link)
    ) {
      return null;
    }
    const provider = settings?.linkFaviconProvider ?? 'google';
    if (provider === 'affine') {
      return { key: `affine:${link}`, provider, url: link };
    }

    const service = new URL('https://www.google.com/s2/favicons');
    service.searchParams.set('domain_url', url.origin);
    service.searchParams.set('sz', '32');
    return { key: `google:${url.origin}`, provider, url: service.toString() };
  }

  private _resetFavicon() {
    this._faviconRequest++;
    this._requestedFaviconKey = null;
    this._faviconUrl = undefined;
    if (this._faviconImage) {
      this._faviconImage.onload = null;
      this._faviconImage.onerror = null;
      this._faviconImage = null;
    }
  }

  private _loadFavicon(src: string, request: number) {
    // Using the same URL per origin lets the browser share and cache images.
    const image = new Image();
    this._faviconImage = image;
    image.onload = () => {
      if (this.isConnected && request === this._faviconRequest) {
        this._faviconUrl = src;
        this._faviconImage = null;
      }
      image.onload = image.onerror = null;
    };
    image.onerror = () => {
      // Failed images leave the original text-only link, without a gap.
      if (request === this._faviconRequest) this._faviconImage = null;
      image.onload = image.onerror = null;
    };
    image.src = src;
  }

  private async _loadAffineFavicon(link: string, request: number) {
    try {
      // Preserve shared request deduplication by ignoring stale results locally.
      const preview = await this.std
        .getOptional(LinkPreviewServiceIdentifier)
        ?.query(link);
      if (!this.isConnected || request !== this._faviconRequest) return;
      const proxy = this.std.getOptional(ImageProxyService);
      if (preview?.icon && proxy) {
        this._loadFavicon(proxy.buildUrl(preview.icon), request);
      }
    } catch {
      // Missing metadata leaves the link text usable.
    }
  }

  override willUpdate() {
    const info = this._faviconRequestInfo;
    const key = info?.key ?? null;
    if (key === this._requestedFaviconKey) return;
    this._resetFavicon();
    this._requestedFaviconKey = key;
    if (info?.provider === 'google') {
      this._loadFavicon(info.url, this._faviconRequest);
    } else if (info) {
      this._loadAffineFavicon(info.url, this._faviconRequest).catch(
        console.error
      );
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._resetFavicon();
  }

  // The link has been identified.
  private _identified: boolean = false;

  // see https://github.com/toeverything/AFFiNE/issues/1540
  private readonly _onMouseUp = () => {
    const anchorElement = this.querySelector('a');
    if (!anchorElement || !anchorElement.isContentEditable) return;
    anchorElement.contentEditable = 'false';
    setTimeout(() => {
      anchorElement.removeAttribute('contenteditable');
    }, 0);
  };

  private _referenceInfo: ReferenceInfo | null = null;

  openLink = (e?: MouseEvent) => {
    if (!this._identified) {
      this._identified = true;
      this._identify();
    }

    const referenceInfo = this._referenceInfo;
    if (!referenceInfo) return;

    const refNodeSlotsProvider = this.std.getOptional(RefNodeSlotsProvider);
    if (!refNodeSlotsProvider) return;

    e?.preventDefault();

    refNodeSlotsProvider.docLinkClicked.next({
      ...referenceInfo,
      openMode: e?.button === 1 ? 'open-in-new-tab' : undefined,
      host: this.std.host,
    });
  };

  _whenHover = whenHover(
    hovered => {
      const message$ = this.std.get(ToolbarRegistryIdentifier).message$;

      if (hovered) {
        message$.value = {
          flavour: 'affine:link',
          element: this,
          setFloating: this._whenHover.setFloating,
        };
        return;
      }

      // Clears previous bindings
      message$.value = null;
      this._whenHover.setFloating();
    },
    { enterDelay: 500 }
  );

  override connectedCallback() {
    super.connectedCallback();

    this._whenHover.setReference(this);

    const message$ = this.std.get(ToolbarRegistryIdentifier).message$;

    this._disposables.add(() => {
      if (message$?.value) {
        message$.value = null;
      }
      this._whenHover.dispose();
    });
  }

  // Workaround for links not working in contenteditable div
  // see also https://stackoverflow.com/questions/12059211/how-to-make-clickable-anchor-in-contenteditable-div
  //
  // Note: We cannot use JS to directly open a new page as this may be blocked by the browser.
  //
  // Please also note that when readonly mode active,
  // this workaround is not necessary and links work normally.
  get block() {
    if (!this.inlineEditor?.rootElement) return null;
    const block = this.inlineEditor.rootElement.closest<BlockComponent>(
      `[${BLOCK_ID_ATTR}]`
    );
    return block;
  }

  get inlineEditor() {
    const inlineRoot = this.closest<InlineRootElement<AffineTextAttributes>>(
      `[${INLINE_ROOT_ATTR}]`
    );
    return inlineRoot?.inlineEditor;
  }

  get link() {
    return normalizeUrl(this.delta.attributes?.link ?? '');
  }

  get selfInlineRange() {
    const selfInlineRange = this.inlineEditor?.getInlineRangeFromElement(this);
    return selfInlineRange;
  }

  // Identify if url is an internal link
  private _identify() {
    const link = this.link;
    if (!link) return;

    const result = this.std.getOptional(ParseDocUrlProvider)?.parseDocUrl(link);
    if (!result) return;

    const { docId: pageId, ...params } = result;

    this._referenceInfo = { pageId, params };
  }

  private _renderLink(style: StyleInfo) {
    const faviconUrl =
      this._faviconRequestInfo?.key === this._requestedFaviconKey
        ? this._faviconUrl
        : undefined;
    return html`<a
      href=${this.link}
      rel="noopener noreferrer"
      target="_blank"
      class=${classMap({ 'affine-link-with-favicon': !!faviconUrl })}
      style=${styleMap({
        ...style,
        '--affine-link-favicon': faviconUrl
          ? `url(${JSON.stringify(faviconUrl)})`
          : undefined,
      })}
      @click=${this.openLink}
      @auxclick=${this.openLink}
      @mouseup=${this._onMouseUp}
      ><v-text .str=${this.delta.insert}></v-text
    ></a>`;
  }

  override render() {
    const linkStyle = {
      color: 'var(--affine-link-color)',
      fill: 'var(--affine-link-color)',
      cursor: 'pointer',
    };

    if (this.delta.attributes && this.delta.attributes?.code) {
      const codeStyle = affineTextStyles(this.delta.attributes);
      return html`<code style=${styleMap(codeStyle)}>
        ${this._renderLink(linkStyle)}
      </code>`;
    }

    const style = this.delta.attributes
      ? affineTextStyles(this.delta.attributes, linkStyle)
      : {};

    return this._renderLink(style);
  }

  @property({ type: Object })
  accessor delta: DeltaInsert<AffineTextAttributes> = {
    insert: ZERO_WIDTH_FOR_EMPTY_LINE,
  };

  @property({ attribute: false })
  accessor std!: BlockStdScope;

  @property({ attribute: false })
  accessor isLinkStart = true;
}
